import { useEffect, useRef, useState } from "react";

import NormalExpenseInput from "../components/NormalExpenseInput";
import ReceiptScanner from "../components/ReceiptScanner";
import {
  categorizeDemoExpense,
  parseDemoExpenseText,
} from "../data/demoData";
import {
  addStoredDemoExpenses,
  deleteStoredDemoExpense,
  getStoredDemoExpenses,
  replaceStoredDemoExpenses,
} from "../utils/demoStorage";
import { BASE_URL } from "../utils/apiConfig";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";
import { buildMonthYearQuery, getMonthName } from "../utils/monthYear";

function Expenses({ selectedMonth, selectedYear }) {
  const smartInputRef = useRef(null);
  const [demoMode, setDemoMode] = useState(
    () => getIsDemoMode()
  );
  const [expenses, setExpenses] = useState([]);

  const [smartText, setSmartText] = useState("");
  const [smartLoading, setSmartLoading] = useState(false);

  const [expenseText, setExpenseText] = useState("");
  const [loading, setLoading] = useState(false);
  const [recategorizing, setRecategorizing] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const loadExpenses = async () => {
    if (demoMode) {
      setExpenses(getStoredDemoExpenses(selectedMonth, selectedYear));
      return;
    }

    try {
      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const res = await fetch(`${BASE_URL}/api/expenses?${query}`, {
        headers: getAuthHeaders(),
      });

      const data = await res.json();

      setExpenses(Array.isArray(data) ? data : []);
    } catch {
      setExpenses([]);
    }
  };

  useEffect(() => {
    setDemoMode(getIsDemoMode());
  }, []);

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, demoMode]);

  const addSmartExpense = async () => {
    if (!smartText.trim()) return;

    setSmartLoading(true);

    try {
      if (demoMode) {
        const parsedExpenses = parseDemoExpenseText(smartText, "ai");

        if (parsedExpenses.length > 0) {
          addStoredDemoExpenses(parsedExpenses, selectedMonth, selectedYear);
          setSmartText("");
          loadExpenses();
        }

        return;
      }

      await fetch(`${BASE_URL}/api/expenses/bulk`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          text: smartText,
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      setSmartText("");
      loadExpenses();
    } catch {
      alert("Failed to add smart expense");
    } finally {
      setSmartLoading(false);
    }
  };

  const addExpense = async () => {
    if (!expenseText.trim()) return;

    setLoading(true);

    try {
      if (demoMode) {
        const parsedExpenses = parseDemoExpenseText(expenseText, "rule");

        if (parsedExpenses.length > 0) {
          addStoredDemoExpenses(parsedExpenses, selectedMonth, selectedYear);
          setExpenseText("");
          loadExpenses();
        }

        return;
      }

      await fetch(`${BASE_URL}/api/expenses/bulk`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          text: expenseText,
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      setExpenseText("");
      loadExpenses();
    } catch {
      alert("Failed to add expense");
    } finally {
      setLoading(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      if (demoMode) {
        deleteStoredDemoExpense(id);
        loadExpenses();
        return;
      }

      await fetch(`${BASE_URL}/api/expenses/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      loadExpenses();
    } catch {
      alert("Failed to delete expense");
    }
  };

  const recategorizeExpenses = async () => {
    setRecategorizing(true);

    try {
      if (demoMode) {
        const recategorizedExpenses = expenses.map((expense) => ({
          ...expense,
          category: categorizeDemoExpense(expense.text || expense.description || expense.category),
          source: expense.source === "ocr" ? "ocr" : "ai",
        }));

        replaceStoredDemoExpenses(selectedMonth, selectedYear, recategorizedExpenses);
        loadExpenses();
        return;
      }

      await fetch(`${BASE_URL}/api/expenses/recategorize`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          month: selectedMonth,
          year: selectedYear,
          onlyOthers: false,
        }),
      });

      loadExpenses();
    } catch {
      alert("Failed to fix expense categories");
    } finally {
      setRecategorizing(false);
    }
  };

  const handleQuickAddExpense = () => {
    if (smartText.trim()) {
      addSmartExpense();
      return;
    }

    smartInputRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    smartInputRef.current?.focus();
  };

  const categories = [
    "all",
    ...new Set(expenses.map((expense) => expense.category || "others")),
  ];

  const filteredExpenses = expenses.filter((expense) => {
    const categoryMatch =
      selectedCategory === "all" ||
      (expense.category || "others") === selectedCategory;
    const sourceMatch =
      selectedSource === "all" || (expense.source || "rule") === selectedSource;

    return categoryMatch && sourceMatch;
  });

  const filteredTotal = filteredExpenses.reduce(
    (sum, expense) => sum + Number(expense.amount || 0),
    0
  );
  const categoryTotals = filteredExpenses.reduce((totals, expense) => {
    const category = expense.category || "others";
    totals[category] = (totals[category] || 0) + Number(expense.amount || 0);
    return totals;
  }, {});
  const topCategory =
    Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || "None";
  const aiCategorizedCount = filteredExpenses.filter(
    (expense) => expense.source === "ai"
  ).length;
  const sourceOptions = [
    "all",
    ...new Set(expenses.map((expense) => expense.source || "rule")),
  ];
  const formatMoney = (value) => Number(value || 0).toLocaleString("en-IN");
  const getSourceLabel = (source) => (source || "rule").toUpperCase();
  const getExpenseDate = (expense) => {
    if (expense.createdAt) {
      return new Date(expense.createdAt).toLocaleDateString("en-IN");
    }

    return `${getMonthName(selectedMonth)} ${selectedYear}`;
  };
  const getCategoryInitial = (category) =>
    (category || "others").trim().charAt(0).toUpperCase() || "O";

  return (
    <div className="page expenses-page expenses-premium-page">
      <div className="expenses-topbar">
        <div>
          <div className="dashboard-title-badges">
            <span className="soft-badge">AI Expense Tracker</span>
            {demoMode && <span className="demo-mode-badge">Demo Mode</span>}
          </div>
          <h2>Expenses</h2>
          <p>
            {demoMode
              ? "Demo sandbox expenses. Changes stay in local sample data only."
              : "Track, categorize, and manage your spending"}
          </p>
        </div>

        <button
          type="button"
          className="glow-button"
          onClick={handleQuickAddExpense}
        >
          + Add Expense
        </button>
      </div>

      {demoMode && (
        <div className="demo-mode-notice">
          Demo changes are saved locally and never sent to your personal expense APIs.
        </div>
      )}

      <div className="expenses-summary-strip">
        <div className="expense-summary-card total">
          <span>Total expenses</span>
          <strong>{"\u20b9"}{formatMoney(filteredTotal)}</strong>
          <p>{getMonthName(selectedMonth)} {selectedYear}</p>
        </div>

        <div className="expense-summary-card">
          <span>Transactions</span>
          <strong>{filteredExpenses.length}</strong>
          <p>{expenses.length} total entries</p>
        </div>

        <div className="expense-summary-card">
          <span>Top category</span>
          <strong>{topCategory}</strong>
          <p>Based on current filters</p>
        </div>

        <div className="expense-summary-card ai">
          <span>AI categorized</span>
          <strong>{aiCategorizedCount}</strong>
          <p>Smart source entries</p>
        </div>
      </div>

      <div className="expenses-workspace-grid expenses-premium-grid">
        <div className="expenses-tools-column">
          <div className="premium-panel expense-panel-card expense-form-card expense-smart-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">AI Smart Entry</span>
                <h3>Smart Expense Input</h3>
              </div>
              <span className="source-tag ai-tag">AI / RULE</span>
            </div>

            <div className="input-box premium-smart-entry">
              <label htmlFor="smart-expense-input">AI Smart Entry</label>

              <div className="input-row">
                <textarea
                  ref={smartInputRef}
                  id="smart-expense-input"
                  value={smartText}
                  onChange={(e) => setSmartText(e.target.value)}
                  placeholder="Example: movie 500, petrol 200, tea 50&#10;groceries 1200, metro 80, coffee 150"
                />

                <button
                  type="button"
                  onClick={addSmartExpense}
                  disabled={smartLoading}
                >
                  {smartLoading ? "Adding..." : "Add"}
                </button>
              </div>
            </div>

            <p className="expense-entry-hint">
              Example: movie 500, petrol 200, tea 50
            </p>
          </div>

          <div className="premium-panel expense-panel-card expense-form-card expense-manual-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Manual Entry</span>
                <h3>Quick Add</h3>
              </div>
            </div>

            <NormalExpenseInput
              expenseText={expenseText}
              setExpenseText={setExpenseText}
              addExpense={addExpense}
              loading={loading}
            />
          </div>

          <ReceiptScanner
            mode={demoMode ? "demo" : "personal"}
            onExpenseAdded={loadExpenses}
            saveToBackend={!demoMode}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
          />
        </div>

        <div className="expenses-list-column">
          <div className="premium-panel expense-panel-card expense-filter-card expenses-filter-panel">
            <div className="filter-header">
              <div>
                <span className="soft-badge">Filters</span>
                <h3>Expense Filters</h3>
                <p>Filtered total: {"\u20b9"}{formatMoney(filteredTotal)}</p>
              </div>

              <button
                type="button"
                className="category-fix-btn"
                onClick={recategorizeExpenses}
                disabled={recategorizing}
              >
                {recategorizing ? "Fixing..." : "Fix Categories"}
              </button>
            </div>

            <div className="expense-filter-grid">
              <label>
                <span>Month</span>
                <select value={selectedMonth} disabled>
                  <option value={selectedMonth}>{getMonthName(selectedMonth)}</option>
                </select>
              </label>

              <label>
                <span>Year</span>
                <select value={selectedYear} disabled>
                  <option value={selectedYear}>{selectedYear}</option>
                </select>
              </label>

              <label>
                <span>Category</span>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c === "all" ? "All Categories" : c}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Source</span>
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                >
                  {sourceOptions.map((source) => (
                    <option key={source} value={source}>
                      {source === "all" ? "All Sources" : getSourceLabel(source)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="premium-panel expense-panel-card expense-list-card expense-list-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Transactions</span>
                <h3>Expense List</h3>
              </div>
              <p>{"\u20b9"}{formatMoney(filteredTotal)}</p>
            </div>

            {filteredExpenses.length === 0 ? (
              <p className="placeholder-text">No expenses found.</p>
            ) : (
              <div className="premium-expense-list">
                {filteredExpenses.map((expense) => (
                  <div
                    key={expense._id}
                    className="premium-expense-item expense-row-premium"
                  >
                    <div className="expense-category-icon">
                      {getCategoryInitial(expense.category)}
                    </div>

                    <div className="premium-expense-main">
                      <div className="premium-expense-title-row">
                        <h4>{expense.text || expense.description || "Expense"}</h4>
                        <strong>{"\u20b9"}{formatMoney(expense.amount)}</strong>
                      </div>

                      <div className="premium-expense-meta">
                        <span className="expense-category-pill">
                          {expense.category || "others"}
                        </span>
                        <span>{getExpenseDate(expense)}</span>
                        <span
                          className={`source-tag ${
                            expense.source === "ai"
                              ? "ai-tag"
                              : expense.source === "ocr"
                              ? "ocr-tag"
                              : "rule-tag"
                          }`}
                        >
                          {getSourceLabel(expense.source)}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="delete-btn"
                      onClick={() => deleteExpense(expense._id)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Expenses;
